import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ChevronLeft, RotateCcw } from "lucide-react";
import type { Article } from "@core/features/support";
import { Button } from "@themes/pollinator/ui/Button";
import { Loader } from "@themes/pollinator/ui/Loader";
import { Type } from "@themes/pollinator/ui/Type";
import { MdxRenderer } from "@themes/pollinator/ui/MdxRenderer";
import { MdxRendererTableOfContents } from "@themes/pollinator/ui/MdxRendererTableOfContents";
import { videoEventBus } from "@core/features/video";

export type Props = {
  article: Article | null;
  scrollContainer?: HTMLElement | null;
  canGoBack: boolean;
  canResetToOriginal: boolean;
  isLoading: boolean;
  loadError: boolean;
  onBack: () => void;
  onResetToOriginal: () => void;
};

export function VideoMeetingMaterials({
  article,
  scrollContainer,
  canGoBack,
  canResetToOriginal,
  isLoading,
  loadError,
  onBack,
  onResetToOriginal,
}: Props) {
  const backBarRef = useRef<HTMLDivElement>(null);
  const [backBarHeight, setBackBarHeight] = useState(0);

  useEffect(() => {
    videoEventBus.emitEvent("videochat:materials:opened", {});
  }, []);

  // Measure the Back-bar height so the TOC can sticky-stack below it instead
  // of colliding at top:0 (the Back bar is opaque + z-20 and would cover it).
  useLayoutEffect(() => {
    if (!canGoBack || !backBarRef.current) {
      setBackBarHeight(0);
      return;
    }
    const node = backBarRef.current;
    const update = () => setBackBarHeight(node.offsetHeight);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, [canGoBack]);

  return (
    <div className="relative flex flex-col pb-8">
      {canGoBack && (
        <div
          ref={backBarRef}
          className="sticky top-0 z-20 flex items-center justify-between gap-2 bg-brand-50 px-4 py-3 border-b border-brand-800"
        >
          <Button
            variant="ghost"
            iconComponent={ChevronLeft}
            onClick={onBack}
            data-testid="materials-back-button"
          >
            Back
          </Button>
          {canResetToOriginal && (
            <Button
              variant="link"
              iconComponent={RotateCcw}
              onClick={onResetToOriginal}
              data-testid="materials-reset-button"
            >
              Back to materials
            </Button>
          )}
        </div>
      )}

      {isLoading && (
        <div className="flex justify-center py-12">
          <Loader />
        </div>
      )}

      {!isLoading && loadError && (
        <div className="px-4 py-8" data-testid="materials-load-error">
          <Type variant="body2" className="text-brand-700">
            We couldn't load that article. Use Back to return to where you were.
          </Type>
        </div>
      )}

      {!isLoading && !loadError && article && (
        <>
          <MdxRendererTableOfContents
            content={article.content || ""}
            className="px-0 py-8 sticky border-b border-brand-800 shadow-brand-sm"
            innerClassName="flex-nowrap px-4"
            scrollContainer={scrollContainer}
            style={{ top: backBarHeight }}
          />
          <MdxRenderer content={article.content || ""} className="px-4 pt-8" />
        </>
      )}
    </div>
  );
}
